import type { ContentCategory, ContentItem } from "@/types/content";
import { filmNews } from "@/mocks/filmNews";
import { entertainmentNews } from "@/mocks/entertainmentNews";
import { movies } from "@/mocks/movies";
import { tvShows } from "@/mocks/tvShows";

export const allContent: ContentItem[] = [
  ...filmNews,
  ...entertainmentNews,
  ...movies,
  ...tvShows,
];

export const getContentById = (id: string): ContentItem | undefined =>
  allContent.find((item) => item.id === id);

export const getByCategory = (category: ContentCategory): ContentItem[] =>
  allContent.filter((item) => item.category === category);

export const searchContent = (keyword: string): ContentItem[] => {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return allContent.filter((item) => {
    const haystack = `${item.title} ${item.summary} ${item.content} ${
      item.genre ?? ""
    }`.toLowerCase();
    return haystack.includes(kw);
  });
};

export const getRelated = (item: ContentItem, limit = 4): ContentItem[] =>
  allContent
    .filter((c) => c.category === item.category && c.id !== item.id)
    .slice(0, limit);

export const getHot = (limit = 5): ContentItem[] =>
  [...allContent].sort((a, b) => b.heat - a.heat).slice(0, limit);

export const getLatest = (limit = 6): ContentItem[] =>
  [...allContent]
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, limit);

export type SortKey = "date-desc" | "date-asc" | "heat-desc";

export const sortContent = (
  list: ContentItem[],
  sort: SortKey
): ContentItem[] => {
  const copied = [...list];
  if (sort === "date-asc") {
    return copied.sort((a, b) => (a.date > b.date ? 1 : -1));
  }
  if (sort === "heat-desc") {
    return copied.sort((a, b) => b.heat - a.heat);
  }
  return copied.sort((a, b) => (a.date < b.date ? 1 : -1));
};

export interface Paginated<T> {
  items: T[];
  total: number;
  totalPages: number;
  page: number;
}

export const paginate = <T,>(
  list: T[],
  page: number,
  pageSize: number
): Paginated<T> => {
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    total,
    totalPages,
    page: safePage,
  };
};